import {TagBaselineDefinitionVersionExtended} from "app/services/domain/tag-baseline-versions.service";
import {TagCoverageSummary} from "app/services/domain/tag-posture.service";
import * as Models from "app/services/proxy/model/models";
import {TagRuleConditionsValidatorDirective, TagRuleRegexValidatorDirective} from "app/tag-governance/baselines/tag-rule-validator.directive";
import {TagRule} from "app/tag-governance/tag-utils";

import {Lookup} from "framework/services/utils.service";
import {ControlOption} from "framework/ui/control-option";
import {inParallel} from "framework/utils/concurrency";

export class TagRemediationHandler
{
    readonly baselineTitleToRule: Lookup<Models.TagBaselineRule> = {};

    private readonly m_allowedValuesUnion = new Set<string>();
    private m_allowedValuesIntersection: Set<string>;

    conditionsRules: Models.TagBaselineRuleCondition[] = [];
    regexes: Models.TagBaselineRuleRegex[]             = [];

    valueOptions(): ControlOption<string>[]
    {
        return this.m_allowedValuesIntersection ? Array.from(this.m_allowedValuesUnion)
                                                       .map((value) => new ControlOption(value, value, null, !this.m_allowedValuesIntersection.has(value))) : null;
    }

    //--//

    private constructor(public readonly key: string)
    {}

    processBaselineRule(definition: Models.TagBaselineDefinition,
                        baselineDetails: Models.TagBaselineConfiguration)
    {
        const details = baselineDetails?.tags[this.key];
        if (details)
        {
            const rule = new TagRule(null, this.key, details);
            if (this.processAllowedValuesRule(rule) || this.processConditionsRule(rule) || this.processRegexRule(rule))
            {
                this.baselineTitleToRule[definition.title] = details;
            }
        }
    }

    private processAllowedValuesRule(rule: TagRule): boolean
    {
        const allowedValuesRule = rule.asAllowedValuesRule();
        if (!allowedValuesRule) return false;

        const values = allowedValuesRule.allowed_values;
        for (let value of values) this.m_allowedValuesUnion.add(value);
        if (!this.m_allowedValuesIntersection)
        {
            this.m_allowedValuesIntersection = new Set(values);
        }
        else
        {
            this.m_allowedValuesIntersection = new Set(values.filter((value) => this.m_allowedValuesIntersection.has(value)));
        }

        this.considerAllowedWithConditions(...this.conditionsRules);
        this.considerAllowedWithRegex(...this.regexes);

        return true;
    }

    private processConditionsRule(rule: TagRule): boolean
    {
        const conditionsRule = rule.asConditionRule();
        if (!conditionsRule) return false;

        this.conditionsRules.push(conditionsRule);

        this.considerAllowedWithConditions(conditionsRule);

        return true;
    }

    private processRegexRule(rule: TagRule): boolean
    {
        const regexRule = rule.asRegexRule();
        if (!regexRule) return false;

        this.regexes.push(regexRule);

        this.considerAllowedWithRegex(regexRule);

        return true;
    }

    private considerAllowedWithConditions(...conditionRules: Models.TagBaselineRuleCondition[])
    {
        if (this.m_allowedValuesIntersection && conditionRules.length)
        {
            this.m_allowedValuesIntersection = new Set(Array.from(this.m_allowedValuesIntersection)
                                                            .filter((value) => TagRuleConditionsValidatorDirective.validateTagValue(conditionRules, value)));
        }
    }

    private considerAllowedWithRegex(...regexes: Models.TagBaselineRuleRegex[])
    {
        if (this.m_allowedValuesIntersection && regexes.length)
        {
            this.m_allowedValuesIntersection = new Set(Array.from(this.m_allowedValuesIntersection)
                                                            .filter((value) => TagRuleRegexValidatorDirective.validateTagValue(regexes, value)));
        }
    }

    //--//

    public static async generateHelperLookup(versionExts: TagBaselineDefinitionVersionExtended[],
                                             coverage: TagCoverageSummary): Promise<Lookup<TagRemediationHandler>>
    {
        await inParallel(versionExts, (version) => version.getDetails());

        const lookup: Lookup<TagRemediationHandler> = {};
        for (const versionExt of versionExts)
        {
            const definition = await versionExt.getDefinition();
            const details    = await versionExt.getDetails();

            if (!coverage.matchesScope(definition.model.scope)) continue;

            for (const key in details?.tags)
            {
                lookup[key] = TagRemediationHandler.generateHelper(key, definition.model, details, lookup[key]);
            }
        }

        return lookup;
    }

    private static generateHelper(key: string,
                                  definition: Models.TagBaselineDefinition,
                                  details: Models.TagBaselineConfiguration,
                                  helper: TagRemediationHandler): TagRemediationHandler
    {
        helper ||= new TagRemediationHandler(key);
        helper.processBaselineRule(definition, details);
        return helper;
    }
}
